import { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiError, API } from "@/lib/api";
import { TRIBES, tribeForChild } from "@/lib/tribes";
import { Download, ArrowLeft, Share2, Camera, CheckCircle2, Heart } from "lucide-react";
import { certificatePdf, drawCertificateOnCanvas } from "@/lib/pdf";

const TEMPLATE_TRIBES = new Set(["thrive_tribe", "nasal_ninja", "myo_princess"]);
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Certificate() {
  const { childId, sessionNumber } = useParams();
  const [child, setChild] = useState(null);
  const [cert, setCert] = useState(null);
  const [template, setTemplate] = useState(null);
  const [sharing, setSharing] = useState(false);
  const [shareMsg, setShareMsg] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadErr, setUploadErr] = useState("");
  const canvasRef = useRef(null);
  const token = localStorage.getItem("btt_token");

  const load = () =>
    Promise.all([api.get(`/children/${childId}`), api.get(`/certificates/${childId}`), api.get(`/content/overrides`).catch(() => ({ data: {} }))]).then(async ([c, cf, ov]) => {
      setChild(c.data);
      const found = cf.data.find((x) => x.session_number === parseInt(sessionNumber, 10));
      setCert(found);
      // Thrive Story: pull the admin-uploaded per-session cert image from content_overrides
      // (keyed as `{child_id}:{session_number}` with field=image_url under thrive_story_cert).
      // Only renders when story_assets_ready is true — pending families fall through to generic.
      if (found && c.data && c.data.tribe === "thrive_story" && c.data.story_assets_ready) {
        const url = ov?.data?.thrive_story_cert?.[`${c.data.id || childId}:${found.session_number}`]?.image_url;
        if (url) {
          setTemplate({ image_data_url: url, aspect_ratio: 1.4, name_pos: null, date_pos: null });
          return;
        }
      }
      // Fetch the illustrated template for this tribe+level. 404 = generic fallback.
      if (found && c.data && TEMPLATE_TRIBES.has(c.data.tribe)) {
        try {
          const { data } = await api.get(`/certificate-templates/${c.data.tribe}/${found.session_number}`);
          setTemplate(data);
        } catch (_) { setTemplate(null); }
      } else {
        setTemplate(null);
      }
    });

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [childId, sessionNumber]);

  if (!child || !cert) return <div className="min-h-screen shell-bg p-8">Loading…</div>;
  const tribe = tribeForChild(child);
  const dateLabel = new Date(cert.date).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });

  const download = () => {
    const doc = certificatePdf(child, cert);
    doc.save(`certificate-session-${cert.session_number}-${child.name}.pdf`);
  };

  const shareCard = async () => {
    setSharing(true); setShareMsg("");
    try {
      const canvas = canvasRef.current;
      drawCertificateOnCanvas(canvas, child, cert);
      const blob = await new Promise((res) => canvas.toBlob(res, "image/png"));
      const file = new File([blob], `certificate-${child.name}-s${cert.session_number}.png`, { type: "image/png" });
      const shareData = { files: [file], title: `${child.name}'s Session ${cert.session_number}`, text: `${child.name} just finished Session ${cert.session_number} of Breathe to Thrive!` };
      if (navigator.canShare && navigator.canShare(shareData)) {
        await navigator.share(shareData);
        setShareMsg("Shared!");
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = file.name;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
        setShareMsg("Image saved — attach it to a text or email");
      }
    } catch (e) { setShareMsg("Couldn't share — try Download instead"); }
    finally { setSharing(false); setTimeout(() => setShareMsg(""), 4000); }
  };

  const uploadProof = async (file) => {
    if (!file) return;
    setUploading(true); setUploadErr("");
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/certificates/${cert.id}/proof`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await load();
    } catch (e) { setUploadErr(formatApiError(e)); }
    finally { setUploading(false); }
  };

  const proofPath = cert.photo_path;
  const credited = cert.donation_credited;

  return (
    <div className={`min-h-screen tribe-${child.tribe || "neutral"} p-6`}>
      <Link to={`/child/${childId}`} className="inline-flex items-center gap-2 text-sm opacity-70 mb-6" style={{ color: tribe.ink }}><ArrowLeft size={14} /> Back</Link>
      {template && template.image_data_url ? (
        // Illustrated template view — image is the background, name/date rendered as
        // absolute overlays at the admin-positioned coordinates. Uses cqw units so
        // font size scales together with the container width.
        <div
          data-testid="certificate-template-view"
          className="max-w-3xl mx-auto rounded-3xl overflow-hidden relative bg-warm-white shadow-[0_20px_50px_rgba(44,45,91,0.15)]"
          style={{ aspectRatio: template.aspect_ratio || 1.4, containerType: "inline-size" }}
        >
          <img src={template.image_data_url} alt="Certificate template" className="absolute inset-0 w-full h-full object-contain pointer-events-none" draggable={false} />
          {["name_pos", "date_pos"].map((k) => {
            const p = template[k];
            if (!p) return null;
            const value = k === "name_pos" ? child.name : dateLabel;
            const testid = k === "name_pos" ? "cert-template-name" : "cert-template-date";
            return (
              <div
                key={k}
                data-testid={testid}
                className="absolute whitespace-nowrap"
                style={{
                  left: `${p.x}%`,
                  top: `${p.y}%`,
                  transform: "translate(-50%, -50%)",
                  fontSize: `${p.font_size}cqw`,
                  color: p.color || "#2C2D5B",
                  fontWeight: p.font_weight || 700,
                  fontFamily: p.font_family || "Georgia, serif",
                  pointerEvents: "none",
                }}
              >
                {value}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="max-w-3xl mx-auto rounded-3xl p-10 md:p-16 text-center" style={{ background: tribe.surface, border: `4px solid ${tribe.accent}`, color: tribe.ink }} data-testid="certificate-view">
          {(child.is_self || child.tribe !== "neutral") && (
            <div className="text-xs uppercase tracking-[0.3em] font-bold" style={{ color: tribe.accent }}>{child.is_self ? "Certificate of Progress" : tribe.name}</div>
          )}
          <h1 className="font-display text-3xl md:text-4xl font-bold mt-4">Certificate of Session</h1>
          <div className="mt-8 text-sm opacity-80">This certifies that</div>
          <div className="font-display text-5xl md:text-6xl font-bold mt-4" style={{ color: tribe.accent }}>{child.name}</div>
          <div className="mt-6 text-lg">has completed <b>Session {cert.session_number}</b> of Breathe to Thrive</div>
          <div className="mt-2 text-sm opacity-80">{dateLabel}</div>
          <div className="mt-10 italic opacity-70 text-sm">Progress, not perfection.</div>
        </div>
      )}

      <div className="max-w-3xl mx-auto mt-6 flex flex-wrap justify-center gap-3">
        <button data-testid="download-cert-btn" onClick={download} className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold" style={{ background: tribe.accent, color: "#1E1E2E" }}>
          <Download size={16} /> Download PDF
        </button>
        <button data-testid="share-cert-btn" onClick={shareCard} disabled={sharing} className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold border-2" style={{ borderColor: tribe.accent, color: tribe.ink, background: "transparent" }}>
          <Share2 size={16} /> {sharing ? "Preparing…" : "Share card"}
        </button>
      </div>
      {shareMsg && <div className="max-w-3xl mx-auto mt-3 text-center text-sm" style={{ color: tribe.ink }} data-testid="share-msg">{shareMsg}</div>}

      {/* Donation proof section */}
      <div className="max-w-3xl mx-auto mt-8 rounded-3xl p-6 md:p-8" style={{ background: tribe.panel, border: `2px solid ${tribe.accent}`, color: tribe.panel_ink }} data-testid="proof-section">
        <div className="flex items-center gap-2">
          <Heart size={16} style={{ color: tribe.accent }} />
          <div className="font-display text-lg font-bold" style={{ color: tribe.ink }}>Unlock the $1 donation</div>
        </div>
        <p className="mt-2 text-sm opacity-80" style={{ color: tribe.ink }}>
          Upload a photo of your child <b>holding, drawing, or celebrating with</b> the certificate. Once submitted, $1 is added to your family's running donations counter — real proof, real impact.
        </p>

        {proofPath && (
          <div className="mt-4">
            <div className="text-xs uppercase tracking-widest font-semibold" style={{ color: tribe.accent }}>Submitted photo</div>
            <div className="mt-2 rounded-2xl overflow-hidden border" style={{ borderColor: tribe.accent, maxWidth: 320 }}>
              <img src={`${API}/files/${proofPath}?auth=${token}`} alt="proof" className="w-full" />
            </div>
            {credited && (
              <div className="mt-3 inline-flex items-center gap-2 text-sm font-semibold px-3 py-1.5 rounded-full" style={{ background: tribe.accent, color: "#1E1E2E" }} data-testid="donation-credited">
                <CheckCircle2 size={14} /> $1 added to family donations
              </div>
            )}
          </div>
        )}

        <div className="mt-5">
          <label className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-sm cursor-pointer" style={{ background: tribe.accent, color: "#1E1E2E" }} data-testid="upload-proof-label">
            <Camera size={14} /> {proofPath ? "Replace proof photo" : uploading ? "Uploading…" : "Upload proof photo"}
            <input type="file" accept="image/*" className="hidden" data-testid="upload-proof-input" disabled={uploading} onChange={(e) => e.target.files[0] && uploadProof(e.target.files[0])} />
          </label>
          {uploadErr && <div className="mt-2 text-xs text-red-500">{uploadErr}</div>}
        </div>
      </div>

      <canvas ref={canvasRef} className="hidden" />
    </div>
  );
}

